// Phase 7's data-driven faction interactions.
//
// The Phase 4 Human and High Elf trust chains are wired by hand in App.tsx —
// one dedicated handler per step. That does not scale to five more allied
// realms and seven hostile campaigns, so Phase 7's factions are expressed as
// data instead: each FactionAction declares the requirements that decide when
// its button appears and the effects that decide what it does. This module
// answers "what can the player do for this faction, standing here, right now?"
// and folds a chosen action's effects into state. Days spent, the dialogue
// shown, and any battle fought afterwards are the caller's job (App.tsx), for
// symmetry with how every other day-consuming action already works.

import type {
  FactionAction,
  FactionActionRequirements,
  FactionActionShortcut,
  GameState,
  LoopState,
  PermanentState,
} from "../types"
import { factionActions } from "./content"
import { getAbilityLevel, isAtLeastLevel } from "./abilityEngine"
import { isTrustAtLeast } from "./factionEngine"
import {
  ARMY_SCALE,
  grantCommandPoints,
  grantKnowledge,
  grantUnitsAtLocation,
  raiseFactionTrust,
  setWorldFlag,
  subjectFaction,
} from "./loopEngine"

export interface AvailableFactionAction {
  action: FactionAction
  /** True when every requirement is met and the action can be taken now. */
  unlocked: boolean
  /** Why it is locked, when it is shown greyed-out rather than hidden. */
  lockedHint: string | null
  /** Days this will actually cost — the full costDays, or the shortcut's when one is active. */
  costDays: number
  /** The shortcut currently cutting the cost, when the player's knowledge has earned it. */
  activeShortcut: FactionActionShortcut | null
}

/**
 * The Phase 9 knowledge shortcut in play for an action, or null. A shortcut is
 * active exactly when the player permanently holds one of the ids that record
 * having done this before — the negotiation won, the road found, the door
 * located in some earlier loop.
 */
export function getActiveShortcut(
  action: FactionAction,
  permanent: PermanentState,
): FactionActionShortcut | null {
  const shortcut = action.shortcut
  if (!shortcut) return null
  return shortcut.knowledgeAny.some((id) => permanent.knowledge.includes(id))
    ? shortcut
    : null
}

/** The days an action will actually consume, after any active knowledge shortcut. */
export function getFactionActionCostDays(
  action: FactionAction,
  permanent: PermanentState,
): number {
  const base = action.costDays ?? 0
  const shortcut = getActiveShortcut(action, permanent)
  // Never let a mis-authored shortcut raise a cost: a shortcut only ever cuts.
  return shortcut ? Math.min(base, shortcut.costDays) : base
}

function meetsRequirements(
  requires: FactionActionRequirements | undefined,
  permanent: PermanentState,
  loop: LoopState,
): boolean {
  if (!requires) return true
  if (requires.trust && !isTrustAtLeast(loop, requires.trust.factionId, requires.trust.level)) {
    return false
  }
  if (
    requires.ability &&
    !isAtLeastLevel(getAbilityLevel(permanent, requires.ability.abilityId), requires.ability.level)
  ) {
    return false
  }
  if (requires.knowledge && !requires.knowledge.every((id) => permanent.knowledge.includes(id))) {
    return false
  }
  if (requires.flags && !requires.flags.every((flag) => loop.worldFlags.includes(flag))) {
    return false
  }
  if (
    requires.subjectedFactions &&
    !requires.subjectedFactions.every((id) => loop.subjectedFactions.includes(id))
  ) {
    return false
  }
  return true
}

/** Whether an action should be shown at all (before checking if it is unlocked). */
function isVisible(action: FactionAction, permanent: PermanentState, loop: LoopState): boolean {
  if (
    action.hiddenWhenTrustAtLeast &&
    isTrustAtLeast(loop, action.hiddenWhenTrustAtLeast.factionId, action.hiddenWhenTrustAtLeast.level)
  ) {
    return false
  }
  if (action.hiddenWhenFlags?.some((flag) => loop.worldFlags.includes(flag))) return false
  if (action.hiddenWhenKnowledge?.some((id) => permanent.knowledge.includes(id))) return false
  return true
}

/**
 * The faction actions offered at a location. A locked action is only returned
 * when it carries a `lockedHint` to explain itself — otherwise it is simply
 * withheld until its moment, rather than cluttering the screen with steps the
 * player cannot yet see the point of.
 */
export function getFactionActionsAt(
  permanent: PermanentState,
  loop: LoopState,
  locationId: string,
): AvailableFactionAction[] {
  return factionActions
    .filter((action) => action.locationId === locationId && isVisible(action, permanent, loop))
    .map((action): AvailableFactionAction => ({
      action,
      unlocked: meetsRequirements(action.requires, permanent, loop),
      lockedHint: action.lockedHint ?? null,
      costDays: getFactionActionCostDays(action, permanent),
      activeShortcut: getActiveShortcut(action, permanent),
    }))
    .filter((entry) => entry.unlocked || entry.lockedHint !== null)
}

export function getFactionActionById(actionId: string): FactionAction | undefined {
  return factionActions.find((action) => action.id === actionId)
}

/**
 * Folds an action's effects into state: trust raised, factions subjected,
 * knowledge and Command Points granted, contingents left waiting, flags set.
 * Trust and subjection go through loopEngine so alliance-sabotage forgeries
 * (Phase 6) still block trust the same way they do for the hand-wired chains.
 */
export function applyFactionActionEffects(state: GameState, action: FactionAction): GameState {
  const effects = action.effects
  if (!effects) return state
  let next = state
  if (effects.raiseTrust) {
    next = raiseFactionTrust(next, effects.raiseTrust.factionId, effects.raiseTrust.level)
  }
  if (effects.subjectFaction) {
    next = subjectFaction(next, effects.subjectFaction)
  }
  if (effects.grantKnowledge) {
    next = grantKnowledge(next, effects.grantKnowledge)
  }
  if (effects.setFlags) {
    next = effects.setFlags.reduce((current, flag) => setWorldFlag(current, flag), next)
  }
  if (effects.grantCommandPoints) {
    next = grantCommandPoints(next, effects.grantCommandPoints * ARMY_SCALE)
  }
  if (effects.grantUnits) {
    // A lent contingent is sized to the war it is joining — the authored count
    // is the tabletop number, scaled up to the field like everything else.
    next = effects.grantUnits.reduce(
      (current, grant) =>
        grantUnitsAtLocation(current, grant.locationId, grant.unitId, grant.count * ARMY_SCALE),
      next,
    )
  }
  return next
}
